import { get as lodashGet } from 'lodash'
import {
  KV_LIVE_STATS_UNLEASH_FLAG, CountingSchemes, STATS_TYPES, AGGREGATION_LEVELS,
} from '@ahaslides-product/frontend-toolkit'
import { slideType } from '@/constant/allSlideTypes'
import SlideApi from '@/api/slide'
import { CATEGORY_OPTION_LENGTH } from '@/constant/Categorise.constant'
import sentry from '@/services/sentry'

const LIVESTAT_SUBSCRIBE_STATS_ACTION = 'liveStatsV2/subscribeStatsAction'
const LIVESTAT_UNSUBSCRIBE_STATS_ACTION = 'liveStatsV2/unsubscribeStatsAction'
const RETIRE_LIVE_API_UNLEASH_FLAG = 'liveStatsV2/canRetireLiveApi'

const services = {
  updateCategories: async (slideId, categories) => {
    await SlideApi.patchCategoriesBySlideId(slideId, { categories })
  },
  generateCategory: () => ({ id: window.crypto.randomUUID(), value: '', options: [] }),
}

export const _getters = {
  categoriseSlides: (_, __, rootState) => {
    const slides = lodashGet(rootState, 'presentation.slides', [])
    return slides.filter((slide) => slide.type === slideType.categorise)
  },
  slideActiveCategories: (_, __, rootState) => {
    const defaultCategories = JSON.stringify([
      services.generateCategory(),
      services.generateCategory(),
    ])
    const categoryRawData = lodashGet(rootState, 'slideActive.matchingQuestionOptions', defaultCategories) || defaultCategories
    const categories = JSON.parse(categoryRawData)
    if (categories.length === 0) {
      return [services.generateCategory(), services.generateCategory()]
    }

    if (categories.length === 1) {
      return [...categories, { ...services.generateCategory() }]
    }
    return categories
  },

}

const actions = {
  async subscribeCategoriseStatsV2Action({ dispatch, rootGetters }, { slide }) {
    if (!rootGetters[KV_LIVE_STATS_UNLEASH_FLAG]) {
      return null
    }
    const ids = { slideId: slide.id, slideVersion: slide.version }
    const answerValues = []
    JSON.parse(slide.matchingQuestionOptions)?.forEach((category) => {
      category.options?.forEach((option) => {
        answerValues.push(`${category.value}*|*${option.value}`)
      })
    })
    const counting = {
      keys: [
        CountingSchemes.correctAnswerCountPerActivity(ids),
        ...answerValues.map((answerValue) => CountingSchemes.correctAnswerCountPerAnswerValue({
          ...ids, questionId: 0, answerValue,
        })),
        CountingSchemes.incorrectAnswerCountPerActivity(ids),
        ...answerValues.map((answerValue) => CountingSchemes.incorrectAnswerCountPerAnswerValue({
          ...ids, questionId: 0, answerValue,
        })),
      ],
    }
    dispatch('counting/subscribe', {
      ...counting,
      onError: (e) => sentry.captureException(e),
    }, { root: true })

    return function unsubscribeFn() {
      dispatch('counting/unsubscribe', counting, { root: true })
    }
  },

  async subscribeCategoriseStatsAction({ dispatch, rootGetters }, slide) {
    if (rootGetters[RETIRE_LIVE_API_UNLEASH_FLAG]) {
      return dispatch('subscribeCategoriseStatsV2Action', { slide })
    }

    if (rootGetters[KV_LIVE_STATS_UNLEASH_FLAG]) {
      await dispatch('liveStatsV2/fetchStatsByKeyAction', {
        statsKeys: [
          `${STATS_TYPES.CORRECT_ANSWER_COUNT}/${AGGREGATION_LEVELS.PER_ACTIVITY}/${slide.id}/${slide.version}`,
          `${STATS_TYPES.INCORRECT_ANSWER_COUNT}/${AGGREGATION_LEVELS.PER_ACTIVITY}/${slide.id}/${slide.version}`,
        ],
      }, { root: true })

      await dispatch('liveStatsV2/fetchStatsByWildCardAction', {
        path: `${STATS_TYPES.CORRECT_ANSWER_COUNT}/${AGGREGATION_LEVELS.PER_ANSWER_VALUE}/${slide.id}/${slide.version}`,
      }, { root: true })

      await dispatch('liveStatsV2/fetchStatsByWildCardAction', {
        path: `${STATS_TYPES.INCORRECT_ANSWER_COUNT}/${AGGREGATION_LEVELS.PER_ANSWER_VALUE}/${slide.id}/${slide.version}`,
      }, { root: true })
    }

    dispatch(LIVESTAT_SUBSCRIBE_STATS_ACTION, {
      identifier: slide.id,
      statsType: STATS_TYPES.CORRECT_ANSWER_COUNT,
      aggregationLevel: AGGREGATION_LEVELS.PER_ACTIVITY,
    }, { root: true })

    dispatch(LIVESTAT_SUBSCRIBE_STATS_ACTION, {
      identifier: slide.id,
      statsType: STATS_TYPES.INCORRECT_ANSWER_COUNT,
      aggregationLevel: AGGREGATION_LEVELS.PER_ACTIVITY,
    }, { root: true })

    dispatch(LIVESTAT_SUBSCRIBE_STATS_ACTION, {
      identifier: slide.id,
      statsType: STATS_TYPES.CORRECT_ANSWER_COUNT,
      aggregationLevel: AGGREGATION_LEVELS.PER_ANSWER_VALUE,
    }, { root: true })

    dispatch(LIVESTAT_SUBSCRIBE_STATS_ACTION, {
      identifier: slide.id,
      statsType: STATS_TYPES.INCORRECT_ANSWER_COUNT,
      aggregationLevel: AGGREGATION_LEVELS.PER_ANSWER_VALUE,
    }, { root: true })

    const slideId = slide.id
    return function unsubscribeFn() {
      dispatch('unsubscribeCategoriseStatsAction', { slideId })
    }
  },

  unsubscribeCategoriseStatsAction({ dispatch }, { slideId }) {
    const subscriptions = [
      {
        statsType: STATS_TYPES.CORRECT_ANSWER_COUNT,
        aggregationLevel: AGGREGATION_LEVELS.PER_ACTIVITY,
      },
      {
        statsType: STATS_TYPES.INCORRECT_ANSWER_COUNT,
        aggregationLevel: AGGREGATION_LEVELS.PER_ACTIVITY,
      },
      {
        statsType: STATS_TYPES.CORRECT_ANSWER_COUNT,
        aggregationLevel: AGGREGATION_LEVELS.PER_ANSWER_VALUE,
      },
      {
        statsType: STATS_TYPES.INCORRECT_ANSWER_COUNT,
        aggregationLevel: AGGREGATION_LEVELS.PER_ANSWER_VALUE,
      },
    ]

    subscriptions.forEach((sub) => {
      dispatch(LIVESTAT_UNSUBSCRIBE_STATS_ACTION, {
        identifier: slideId,
        ...sub,
      }, { root: true })
    })
  },

  async updateCategoriesAction({ commit, rootState }, payload) {
    const { slideActive } = rootState
    if (!slideActive) return
    const matchingQuestionOptions = JSON.stringify(payload)
    const nonEmptyCategories = payload.filter((category) => category && category.value && category.value.trim())
    if (nonEmptyCategories && nonEmptyCategories.length > 0) {
      nonEmptyCategories.forEach((category) => {
        category.options = category.options.filter(
          (option) => option.value && option.value.trim().length <= CATEGORY_OPTION_LENGTH,
        )
      })
    }
    commit('updateSlideActive', { matchingQuestionOptions }, { root: true })
    await services.updateCategories(slideActive.id, nonEmptyCategories)
  },
}

const mutations = {

}

const categorise = {
  namespaced: true,
  getters: _getters,
  actions,
  mutations,
}

export default categorise
